/** Row shape returned by `apiService.listPlaybooksForAgent` (archive order = Record N). */
export type PlaybookArchiveRow = {
  id: string;
  paragraph_text: string;
  paragraph_preview: string;
  filename: string;
  author: string;
  side?: string;
};

export type PlaybookRecordIndex = {
  byNumber: Map<number, PlaybookArchiveRow>;
  byId: Map<string, PlaybookArchiveRow>;
  maxNumber: number;
};

/** 1-based record numbers match `--- Record N ---` headers in playbook context. */
export function buildPlaybookRecordIndex(rows: PlaybookArchiveRow[]): PlaybookRecordIndex {
  const byNumber = new Map<number, PlaybookArchiveRow>();
  const byId = new Map<string, PlaybookArchiveRow>();
  rows.forEach((row, i) => {
    byNumber.set(i + 1, row);
    if (row.id) byId.set(row.id, row);
  });
  return { byNumber, byId, maxNumber: rows.length };
}

export function getPlaybookRecord(
  index: PlaybookRecordIndex,
  recordNum: number,
): PlaybookArchiveRow | undefined {
  return index.byNumber.get(recordNum);
}

/** Maps playbook row id → 1-based archive record number (`--- Record N ---`). */
export function buildPlaybookRecordNumberById(
  rows: PlaybookArchiveRow[],
): Map<string, number> {
  const map = new Map<string, number>();
  rows.forEach((row, i) => {
    if (row.id) map.set(row.id, i + 1);
  });
  return map;
}

/**
 * Maps clause match rank (Match 1, Match 2, …) → archive record number.
 * Used when the model cites "Record 1" but meant the first matched negotiation.
 */
export function buildMatchRankToArchiveRecordNum(
  options: Array<{ id: string }>,
  recordNumberById: Map<string, number>,
  maxMatches = 5,
): Map<number, number> {
  const map = new Map<number, number>();
  options.slice(0, maxMatches).forEach((opt, i) => {
    const rank = i + 1;
    const archiveNum = recordNumberById.get(opt.id);
    if (archiveNum != null) {
      map.set(rank, archiveNum);
    }
  });
  return map;
}

/**
 * Resolve a cited record number for UI linking.
 * When match rank N maps to archive record M ≠ N, treat "Record N" as M — the
 * model often cites match rank instead of the archive record number.
 */
export function resolveCitationRecordNum(
  citedNum: number,
  matchRankToArchiveRecordNum?: Map<number, number>,
): number {
  const aliased = matchRankToArchiveRecordNum?.get(citedNum);
  if (aliased != null && aliased !== citedNum) {
    return aliased;
  }
  return citedNum;
}
